import useSessionStore from '@/stores/useSessionStore'
import { loadScript } from '@/utils/loadScript'

import useExchangeFacebookToken, {
  FacebookTokenExchangeResponse,
} from '../api/useExchangeFacebookToken'

const FACEBOOK_APP_ID = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '472446362839997'
const FACEBOOK_API_VERSION = process.env.NEXT_PUBLIC_FACEBOOK_API_VERSION || 'v22.0'
const FACEBOOK_SCRIPT_ID = 'facebook-jssdk'
const FACEBOOK_SCRIPT_SRC = 'https://connect.facebook.net/en_US/sdk.js'

const initFacebookSDK = (): Promise<void> =>
  new Promise((resolve) => {
    const attemptInit = (): void => {
      if (window?.FB && typeof window.FB.init === 'function') {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          version: FACEBOOK_API_VERSION,
        })
        resolve()
        return
      }

      setTimeout(attemptInit, 100)
    }

    attemptInit()
  })

let facebookInitPromise: Promise<void> | null = null

const ensureFacebookSDK = (): Promise<void> => {
  if (facebookInitPromise) return facebookInitPromise

  facebookInitPromise = loadScript({
    id: FACEBOOK_SCRIPT_ID,
    src: FACEBOOK_SCRIPT_SRC,
    crossOrigin: 'anonymous',
  })
    .then(() => initFacebookSDK())
    .catch((error) => {
      facebookInitPromise = null
      throw error
    })

  return facebookInitPromise
}

export const useFacebookAuth = (
  onSuccessCallback?: (response: FacebookTokenExchangeResponse) => void,
): {
  initiateFacebookAuth: () => void
  facebookTokenExchangeError: Error | null
} => {
  const { mutate: exchangeFacebookToken, error: facebookTokenExchangeError } =
    useExchangeFacebookToken()
  const setAuth = useSessionStore((store) => store.setAuth)

  /**
   * Handles the Facebook OAuth response by sending the access token to our
   * Rails backend API for verification and user authentication.
   */
  const handleFacebookResponse = ({ accessToken, userID }: FacebookAuthResponse): void => {
    exchangeFacebookToken(
      { access_token: accessToken, frontend_app: true, user_id: userID },
      {
        // If useFacebookAuth response is successful, we have the x-user-token
        // so set it to the store for reactive changes elsewhere
        onSuccess: (response) => {
          // if the user profile needs to be completed, render the modal
          if (!response.auth_token || !response.user_id) {
            // TODO: show the modal form prepopulated with the user data
            // to complete the user profile
            throw new Error('User profile completion not implemented. User needs to be created.')
          }
          setAuth(response.auth_token, response.user_id, 'Facebook User')
          onSuccessCallback?.(response)
        },
      },
    )
  }

  /**
   * Initiates the Facebook OAuth popup flow
   */
  const initiateFacebookAuth = (): void => {
    const scope = 'public_profile,email'

    void ensureFacebookSDK().then(() => {
      // TODO: Handle the case where FB.login is not available
      if (window && window.FB && typeof window.FB.login === 'function') {
        window.FB.login(
          (response) => {
            if (response.authResponse) {
              handleFacebookResponse(response.authResponse)
            }
          },
          { scope },
        )
      }
    })
  }

  return { initiateFacebookAuth, facebookTokenExchangeError }
}
